<script setup lang="ts">
import { SEOProps, getHeaderData } from '~/helpers/seo'
import { Document } from '@contentful/rich-text-types'
import { BREAKPOINTS } from '~/config/common'
import type { EntryCollection } from 'contentful'

type PageProps = {
  seo?: SEOProps
  fields: {
    title?: string
    description?: Document
    category: string
    slug: string
  }
}

const props = defineProps<PageProps>()
const { locale } = useI18n()
const router = useRouter()
const { query, path } = useRoute()
const { getEntries } = useContentful()

const isConnectedOrArticle = path.includes('/connected') || path.includes('/article')
const featuredBlogArticlePanelData = {
  connectedContentPillar: {
    fields: {
      category: props.fields.category,
      slug: props.fields.slug,
    },
  },
  limit: 1,
  showAsCarousel: false,
}

const articleContentPillarCategories = ['Style Guide', 'Bridal Guide', 'Jewellery Vault', 'Insider', 'Community']
const categoryToSearch =
  props.fields.category === 'All Articles' ? articleContentPillarCategories : [props.fields.category]

const tagToURL = (tag: string) => tag.toLowerCase().replace(' ', '-').replace("'", '')
const filterTag = (tag: string, tags: string[] | undefined) => tag && tags?.find((t) => tag === tagToURL(t))

const { data } = await useAsyncData(`ContentPillarPage:${props.fields.category}`, handler, {
  transform,
  deep: false,
})

async function handler() {
  const entries = await getEntries({
    content_type: 'blogArticle',
    'fields.category[in]': categoryToSearch,
    locale: localeConfig[locale.value].code,
  })
  return entries
}

function transform(entries: EntryCollection<any> | undefined) {
  if (!entries || !entries.items.length) return {}
  const { items } = entries
  const tags: string[] = []
  const articles = items
    ?.filter((article: any) => article.sys.contentType) // remove invalid items
    ?.map((article: any) => {
      tags.push(...(article?.fields?.tags || []))
      return {
        picture: article.fields.heroImage,
        description: article.fields.shortDescription,
        title: article.fields.title,
        tags: article.fields.tags,
        url: article.fields.slug,
        publishedDate: article.fields.publishedDate,
      }
    })

  return { articles, tags: [...new Set(tags)] }
}

const activeFilter = ref<string | null>()
const filteredArticles = ref(data.value?.articles)

watch(activeFilter, (newFilter, _) => {
  if (!newFilter) return
  filteredArticles.value = data.value?.articles?.filter((article) => filterTag(newFilter, article?.tags))
  setDefaultLimit()
  router.push({ query: { tags: tagToURL(newFilter) } })
})

const MOBILE_PER_PAGE = 8
const DESKTOP_PER_PAGE = 16
const limit = ref(MOBILE_PER_PAGE)
function setDefaultLimit() {
  limit.value = window?.innerWidth >= BREAKPOINTS.desktopXL ? DESKTOP_PER_PAGE : MOBILE_PER_PAGE
}

function loadMore() {
  const loadCount = window?.innerWidth >= BREAKPOINTS.desktopXL ? DESKTOP_PER_PAGE : MOBILE_PER_PAGE
  limit.value += loadCount
}

onMounted(() => {
  setDefaultLimit()
  const tagQueryString = query.tags as string
  const queryFilter = filterTag(tagQueryString, data?.value?.tags)?.length ? tagQueryString : null
  activeFilter.value = queryFilter
})

useHead(
  getHeaderData({
    seo: props?.seo,
    locale: locale.value,
    path,
  }),
)
</script>

<template>
  <article class="content-pillar">
    <div class="container">
      <div class="content-pillar__header">
        <h1>{{ fields?.title }}</h1>
        <RichText class="content-pillar__description" v-if="fields.description" :document="fields.description" />
      </div>
      <FeaturedBlogArticlePanel v-bind="featuredBlogArticlePanelData" />
    </div>
    <div class="content-pillar__filters scroller">
      <ul>
        <li v-for="tag in data?.tags">
          <Button
            @click="activeFilter = tagToURL(tag)"
            appearance="Link"
            variant="tertiary"
            :class="{ 'content-pillar__active-filter': activeFilter === tagToURL(tag) }"
          >
            {{ tag }}
          </Button>
        </li>
      </ul>
    </div>

    <div class="container">
      <div class="content-pillar__articles">
        <ArticleCard v-for="article in filteredArticles?.slice(0, limit)" :key="article.url" :item="article" />
      </div>

      <div
        v-if="filteredArticles?.length && filteredArticles?.length > limit"
        class="connected-content-pillar__view-more"
      >
        <Button variant="primary" @click="loadMore">
          {{ $t('connected_content_pillar.button.view_more') }}
        </Button>
      </div>
    </div>
    <div class="content-pillar__categories">
      <h3 class="content-pillar__categories-heading">{{ $t('connected_content_pillar.list_title') }}</h3>
      <div class="scroller">
        <ul>
          <li v-for="category in articleContentPillarCategories">
            <Anchor :href="`/connected/${tagToURL(category)}`">{{ category }}</Anchor>
          </li>
        </ul>
      </div>
    </div>
    <StickySignUpPanel v-if="isConnectedOrArticle" />
  </article>
</template>

<style lang="postcss">
.content-pillar {
  margin-bottom: var(--space-120);

  .content-pillar__header {
    text-align: center;
    padding-bottom: var(--space-120);
  }

  .featured-blog-article-panel {
    padding: 0;

    .article-card.article-card--featured .article-card__image .picture__img {
      max-height: 12.5rem; /** 200px */

      @media (--tablet) {
        max-height: 15.625rem; /** 250px */
      }
      @media (--desktop) {
        max-height: 21.25rem; /** 340px */
      }
    }
  }

  .content-pillar__articles {
    .article-card {
      margin-bottom: var(--space-100);
    }

    @media (--tablet) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--space-20);
    }
    @media (--desktop) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .content-pillar__filters,
  .content-pillar__categories {
    text-align: center;

    ul {
      display: flex;
      gap: var(--space-60);
      margin: 0;
    }

    button,
    a {
      white-space: nowrap;
    }
  }

  .scroller {
    max-width: var(--container-max-width);
    margin-inline: auto;
    position: relative;
    display: flex;
    justify-content: center;

    ul {
      overflow: auto;
      overflow-y: hidden;
      padding-bottom: var(--space-50);
      padding-inline: var(--space-60);
    }

    &::after,
    &::before {
      content: '';
      display: block;
      height: 100%;
      width: var(--space-70);
      position: absolute;
      top: 0;
    }

    &::before {
      left: 0;
      background: linear-gradient(90deg, hsl(0, 0%, 100%), hsla(0, 0%, 100%, 0));
    }
    &::after {
      right: 0;
      background: linear-gradient(90deg, hsla(0, 0%, 100%, 0), hsl(0, 0%, 100%));
    }
  }

  .content-pillar__filters {
    margin-block: var(--space-90) var(--space-70);

    button {
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
    .content-pillar__active-filter {
      text-decoration: underline;
    }
  }

  .connected-content-pillar__view-more {
    margin-block: var(--space-40) var(--space-90);
    display: flex;
    justify-content: center;
  }

  .content-pillar__categories-heading {
    margin-bottom: var(--space-60);
  }
}
</style>
